import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../lib/apiClient';
import { generateThumbnail } from '../lib/thumbnailGenerator';
import type { UploadOptions } from '../lib/authFetch';
import { useAuth } from './useAuth';
import { useStorage } from './useStorage';
import { Video } from '../types/video';

export type UploadResult =
  | { status: 'success'; video: Video }
  | { status: 'storage_exceeded' }
  | { status: 'error'; message: string }
  | { status: 'cancelled' };

/**
 * Multiple components call useVideos() — each gets isolated React state.
 * After upload/delete/share, all listeners must refetch so VideoLibrary stays in sync.
 */
export const AIRECORDING_VIDEOS_REFRESH_EVENT = 'airecording-videos-refresh';

export function broadcastVideosRefresh(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AIRECORDING_VIDEOS_REFRESH_EVENT));
}

export const useVideos = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The last list load failed (not other errors such as a failed delete).
  const [loadFailed, setLoadFailed] = useState(false);
  // Placeholders only until the first load: later refreshes update the list in place, so open
  // cards (share dialog, menus, players) are not replaced while someone uses them.
  const loadedRef = useRef(false);
  const { user } = useAuth();
  const { checkQuota, addStorage } = useStorage();

  const fetchVideos = useCallback(async () => {
    if (!user) {
      setVideos([]);
      setLoading(false);
      loadedRef.current = false;
      return;
    }

    try {
      if (!loadedRef.current) setLoading(true);
      const { data, error } = await apiClient.listVideos();

      if (error) throw error;
      
      // Ensure all videos have a valid duration
      const processedVideos = (data || []).map(video => ({
        ...video,
        duration: video.duration && Number.isFinite(Number(video.duration)) 
          ? Math.round(Number(video.duration)) 
          : 0
      }));
      
      setVideos(processedVideos);
      setLoadFailed(false);
      loadedRef.current = true;
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch videos');
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Keep every useVideos() instance in sync (ScreenRecorder upload vs VideoLibrary list).
  useEffect(() => {
    const onRefresh = () => {
      void fetchVideos();
    };
    window.addEventListener(AIRECORDING_VIDEOS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(AIRECORDING_VIDEOS_REFRESH_EVENT, onRefresh);
  }, [fetchVideos]);

  const getVideoDuration = (blob: Blob): Promise<number> => {
    return new Promise((resolve) => {
      // If not in a browser environment, resolve with 0
      if (typeof document === 'undefined') {
        console.warn('Not in a browser environment, cannot determine video duration');
        resolve(0);
        return;
      }

      const video = document.createElement('video');
      let objectUrl = '';
      
      const cleanup = () => {
        if (objectUrl) {
          window.URL.revokeObjectURL(objectUrl);
        }
        video.remove();
      };
      
      const timer = setTimeout(() => {
        console.warn('Video duration detection timed out');
        cleanup();
        resolve(0);
      }, 5000); // 5 second timeout
      
      video.onloadedmetadata = () => {
        clearTimeout(timer);
        const duration = Math.round(video.duration) || 0;
        console.log('Extracted video duration:', duration, 'seconds');
        cleanup();
        resolve(duration);
      };
      
      video.onerror = (e) => {
        console.error('Error getting video duration:', e);
        clearTimeout(timer);
        cleanup();
        resolve(0);
      };
      
      objectUrl = URL.createObjectURL(blob);
      video.src = objectUrl;
    });
  };

  const uploadVideo = async (
    file: Blob,
    title: string,
    description?: string,
    knownDuration?: number,  // seconds from the recording timer — preferred over blob metadata
    options?: UploadOptions,  // progress and cancel (uploaded files)
  ): Promise<UploadResult> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Check storage quota before uploading
      const hasQuota = await checkQuota(file.size);
      if (!hasQuota) {
        return { status: 'storage_exceeded' };
      }

      // Use timer-based duration when available (avoids the WebM Infinity-duration bug
      // where video.onloadedmetadata returns Infinity for MediaRecorder output).
      const duration = (knownDuration != null && knownDuration > 0)
        ? knownDuration
        : await getVideoDuration(file);
      console.log('Video duration (seconds):', duration);

      // Upload only via backend (eliminates the duplicate frontend direct upload)
      // MP4 (editor's fast export, uploaded files), MOV (uploaded files), else the recorder's WebM.
      const [type, ext] = file.type.includes('mp4') ? ['video/mp4', 'mp4']
        : file.type.includes('quicktime') ? ['video/quicktime', 'mov']
        : ['video/webm', 'webm'];
      const videoFile = new File([file], `${title}.${ext}`, { type });
      const { data, error } = await apiClient.uploadVideo(videoFile, title, description || '', duration, options);

      if (error) throw error;
      if (!data) throw new Error('No video data returned');

      // Register storage consumption with ACV quota system
      await addStorage(file.size);

      broadcastVideosRefresh();

      // Generate and persist thumbnail in the background (non-blocking)
      // Failure here never prevents the video from being saved.
      void (async () => {
        try {
          const thumbnail = await generateThumbnail(file);
          if (!thumbnail) return;
          const { error: thumbError } = await apiClient.uploadVideoThumbnail(data.id, thumbnail);
          if (thumbError) throw thumbError;
          broadcastVideosRefresh(); // refresh cards to show thumbnail
        } catch (err) {
          console.warn('[useVideos] Thumbnail generation/upload failed (non-blocking):', err);
        }
      })();

      return { status: 'success', video: data };
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return { status: 'cancelled' };
      console.error('Upload error:', err);
      const message = err instanceof Error ? err.message : 'Failed to upload video';
      setError(message);
      return { status: 'error', message };
    }
  };

  /** True when the video was deleted. `refresh: false` skips the list refresh (bulk delete does it once). */
  const deleteVideo = async (videoId: string, { refresh = true }: { refresh?: boolean } = {}): Promise<boolean> => {
    try {
      const videoToDelete = videos.find(v => v.id === videoId);
      if (!videoToDelete) throw new Error('Video not found');

      // Moves it to the Recycle Bin. It still counts toward storage; the server releases it
      // when the video is deleted permanently.
      const { error } = await apiClient.deleteVideo(videoId);
      if (error) throw error;

      if (refresh) broadcastVideosRefresh();
      return true;
    } catch (err) {
      console.error('Delete error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete video');
      return false;
    }
  };

  const getSharedVideo = async (token: string): Promise<Video | null> => {
    try {
      const { data, error } = await apiClient.getSharedVideo(token);
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Get shared video error:', err);
      setError(err instanceof Error ? err.message : 'Failed to get shared video');
      return null;
    }
  };

  /** Patch a single video in the local list without a full re-fetch. */
  const updateVideoInList = (videoId: string, patch: Partial<Video>) => {
    setVideos((prev) => prev.map((v) => v.id === videoId ? { ...v, ...patch } : v));
  };

  return {
    videos,
    loading,
    error,
    loadFailed,
    fetchVideos,
    uploadVideo,
    deleteVideo,
    getSharedVideo,
    updateVideoInList,
  };
};